import React, { useCallback, useRef, useState, useMemo } from 'react';
import { DateRange, RangeKeyDict } from 'react-date-range';
import { Popover, Transition } from '@headlessui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendar } from '@fortawesome/pro-regular-svg-icons';
import { faCaretDown } from '@fortawesome/free-solid-svg-icons';
import dayjs from 'dayjs';
import { Colors } from '@core/config';
import CustomDateRangeStyle from '@/components/dashboard/CustomDateRangeStyle';
import { formatDateRangeDisplay } from '@/utils/utils';

export type DateRangeValue = { start: Date; end: Date };

export interface DateRangePickerProps {
  value: DateRangeValue;
  onChange: (start: Date, end: Date) => void;
  /** When true, only call onChange when the popover closes (e.g. Jobs page). When false, call on every selection (e.g. Dashboard). */
  commitOnClose?: boolean;
  placeholder?: string;
  primaryBrandColor?: string;
  wrapperClassName?: string;
  buttonClassName?: string;
  panelMargin?: string;
  minWidth?: string;
  showQuickRanges?: boolean;
  quickRangesContainerClassName?: string;
  quickRangeButtonClassName?: string;
}

const DateRangePicker = ({
  value,
  onChange,
  commitOnClose = false,
  placeholder = 'Select date',
  primaryBrandColor = Colors.LINK_BLUE,
  wrapperClassName = 'flex items-center rounded-lg bg-white px-3 py-3 font-semibold',
  buttonClassName = 'inline-flex min-w-0 cursor-pointer items-center truncate rounded-sm text-sm hover:text-gray-700',
  panelMargin = 'mt-4',
  minWidth,
  showQuickRanges = true,
  quickRangesContainerClassName = 'flex items-center gap-2',
  quickRangeButtonClassName = '',
}: DateRangePickerProps) => {
  const [draft, setDraft] = useState<DateRangeValue>(value);
  const prevOpen = useRef(false);
  const draftRef = useRef(draft);
  draftRef.current = draft;
  const applyQuickRange = useCallback(
    (rangeType: 'today' | 'week' | 'month') => {
      const now = dayjs();
      let nextStartDate = now.startOf('day').toDate();
      let nextEndDate = now.endOf('day').toDate();

      if (rangeType === 'week') {
        nextStartDate = now.startOf('week').startOf('day').toDate();
        nextEndDate = now.endOf('week').endOf('day').toDate();
      }

      if (rangeType === 'month') {
        nextStartDate = now.startOf('month').startOf('day').toDate();
        nextEndDate = now.endOf('month').endOf('day').toDate();
      }

      setDraft({ start: nextStartDate, end: nextEndDate });
      onChange(nextStartDate, nextEndDate);
    },
    [onChange],
  );

  const rangeForCalendar = commitOnClose ? draft : value;
  const quickRangeReference = commitOnClose ? draft : value;

  const activeQuickRange = useMemo(() => {
    const now = dayjs();
    const referenceStart = dayjs(quickRangeReference.start);
    const referenceEnd = dayjs(quickRangeReference.end);

    const isTodayRange =
      referenceStart.isSame(now.startOf('day')) &&
      referenceEnd.isSame(now.endOf('day'));
    if (isTodayRange) {
      return 'today';
    }

    const isWeekRange =
      referenceStart.isSame(now.startOf('week').startOf('day')) &&
      referenceEnd.isSame(now.endOf('week').endOf('day'));
    if (isWeekRange) {
      return 'week';
    }

    const isMonthRange =
      referenceStart.isSame(now.startOf('month').startOf('day')) &&
      referenceEnd.isSame(now.endOf('month').endOf('day'));
    if (isMonthRange) {
      return 'month';
    }

    return null;
  }, [quickRangeReference.end, quickRangeReference.start]);

  // Memoise on the start/end Date references (stable across unrelated parent
  // re-renders, e.g. background pagination fetches) so react-date-range isn't
  // handed a brand new `ranges` array every render, which would otherwise
  // reset the visible calendar month back to the start date.
  const calendarRanges = useMemo(
    () => [
      {
        /* ?? undefined so library doesn't restrict to future-only (unlocks past dates) */
        startDate: rangeForCalendar.start ?? undefined,
        endDate: rangeForCalendar.end ?? undefined,
        key: 'selection',
      },
    ],
    [rangeForCalendar.start, rangeForCalendar.end],
  );

  return (
    <div className="flex items-center">
      {showQuickRanges && (
        <div className={quickRangesContainerClassName}>
          <button
            type="button"
            className={`rounded-lg border px-4 py-3 text-sm font-semibold transition-colors ${
              activeQuickRange === 'today'
                ? 'border-gray-400 bg-gray-100 text-gray-900'
                : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
            } ${quickRangeButtonClassName}`}
            onClick={() => applyQuickRange('today')}
          >
            Today
          </button>
          <button
            type="button"
            className={`rounded-lg border px-4 py-3 text-sm font-semibold transition-colors ${
              activeQuickRange === 'week'
                ? 'border-gray-400 bg-gray-100 text-gray-900'
                : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
            } ${quickRangeButtonClassName}`}
            onClick={() => applyQuickRange('week')}
          >
            This Week
          </button>
          <button
            type="button"
            className={`mr-2 rounded-lg border px-4 py-3 text-sm font-semibold transition-colors ${
              activeQuickRange === 'month'
                ? 'border-gray-400 bg-gray-100 text-gray-900'
                : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
            } ${quickRangeButtonClassName}`}
            onClick={() => applyQuickRange('month')}
          >
            This Month
          </button>
        </div>
      )}
      <CustomDateRangeStyle />
      <Popover className="relative">
        {({ open }) => {
          if (commitOnClose) {
            if (open && !prevOpen.current) {
              setDraft(value);
            }
            if (!open && prevOpen.current) {
              onChange(draftRef.current.start, draftRef.current.end);
            }
            prevOpen.current = open;
          }

          const displayRange = commitOnClose && open ? draft : value;
          const displayText =
            formatDateRangeDisplay(
              dayjs(displayRange.start),
              dayjs(displayRange.end),
            ) || placeholder;

          const chromeClassName = [wrapperClassName, minWidth]
            .filter(Boolean)
            .join(' ');

          return (
            <>
              <div className={chromeClassName}>
                <Popover.Button className={buttonClassName}>
                  <FontAwesomeIcon
                    icon={faCalendar}
                    className="mr-2 shrink-0 text-gray-500"
                    aria-hidden
                  />
                  <span className="truncate">{displayText}</span>
                  <FontAwesomeIcon
                    icon={faCaretDown}
                    className="ml-2 shrink-0 text-gray-500"
                    aria-hidden
                  />
                </Popover.Button>
              </div>
              <Transition
                as={React.Fragment}
                enter="transition ease-out duration-200"
                enterFrom="opacity-0 translate-y-1"
                enterTo="opacity-100 translate-y-0"
                leave="transition ease-in duration-150"
                leaveFrom="opacity-100 translate-y-0"
                leaveTo="opacity-0 translate-y-1"
              >
                <Popover.Panel
                  className={`absolute right-0 z-10 ${panelMargin} rounded-lg border bg-white shadow-lg`}
                >
                  <DateRange
                    ranges={calendarRanges}
                    onChange={(ranges: RangeKeyDict) => {
                      const { startDate: newStart, endDate: newEnd } =
                        ranges.selection;
                      if (commitOnClose) {
                        if (newStart && newEnd) {
                          setDraft({ start: newStart, end: newEnd });
                        } else if (newStart) {
                          setDraft({ start: newStart, end: newStart });
                        }
                      } else {
                        if (newStart && newEnd) {
                          onChange(newStart, newEnd);
                        } else if (newStart) {
                          onChange(newStart, newStart);
                        }
                      }
                    }}
                    moveRangeOnFirstSelection={false}
                    rangeColors={[primaryBrandColor]}
                    showDateDisplay={false}
                    showMonthAndYearPickers={false}
                    className="custom-date-range rounded-lg px-4 shadow-lg"
                  />
                </Popover.Panel>
              </Transition>
            </>
          );
        }}
      </Popover>
    </div>
  );
};

export default DateRangePicker;
